import classNames from "classnames";
import {useEffect, useRef, useState} from "react";

const EYEBROW_ENTER_MS = 260;
const EYEBROW_EXIT_MS = 300;

function defaultEyebrowClasses(tone) {
    const toneClasses = tone === 'success'
        ? "border-emerald-300/90"
        : tone === 'warning'
            ? "border-rose-300/90"
            : "border-slate-200";

    return classNames(
        "auction-eyebrow-chip pointer-events-none absolute -top-[3px] left-1/2 z-10 inline-flex w-auto max-w-[85%] min-w-0 items-center justify-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full border bg-white px-2 py-1 text-[11px] font-bold uppercase leading-none tracking-wide text-slate-900 shadow-sm",
        toneClasses
    );
}

function renderEyebrowDot(tone, className) {
    if (tone === 'success') {
        return <span className={classNames("inline-flex h-2 w-2 shrink-0 rounded-full bg-emerald-400 status-dot-calm", className)} />;
    }

    if (tone === 'warning') {
        return (
            <span className={classNames("relative inline-flex h-2.5 w-2.5 items-center justify-center", className)}>
                <span className="absolute inline-flex h-full w-full rounded-full bg-rose-200" />
                <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400/90 status-dot-alert" />
                <span className="relative inline-flex h-1 w-1 rounded-full bg-rose-700" />
            </span>
        );
    }

    return <span className={classNames("inline-flex h-2 w-2 rounded-full bg-white/80", className)} />;
}

export default function ActionCallout({
    eyebrow,
    eyebrowTone = 'neutral',
    eyebrowKey,
    helperText,
    className,
    eyebrowClassName,
    eyebrowDotClassName,
    helperClassName,
    children
}) {
    const [renderedEyebrow, setRenderedEyebrow] = useState(() => eyebrow
        ? {
            label: eyebrow,
            tone: eyebrowTone,
            key: eyebrowKey ?? eyebrow
        }
        : null
    );
    const [eyebrowPhase, setEyebrowPhase] = useState(eyebrow ? 'enter' : 'hidden');
    const eyebrowTimeoutRef = useRef(null);

    useEffect(() => {
        return () => {
            if (eyebrowTimeoutRef.current) {
                clearTimeout(eyebrowTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const nextKey = eyebrowKey ?? eyebrow ?? null;

        if (eyebrowTimeoutRef.current) {
            clearTimeout(eyebrowTimeoutRef.current);
            eyebrowTimeoutRef.current = null;
        }

        if (eyebrow) {
            if (
                !renderedEyebrow
                || renderedEyebrow.key !== nextKey
                || renderedEyebrow.label !== eyebrow
                || renderedEyebrow.tone !== eyebrowTone
            ) {
                setRenderedEyebrow({
                    label: eyebrow,
                    tone: eyebrowTone,
                    key: nextKey
                });
                setEyebrowPhase('enter');

                eyebrowTimeoutRef.current = setTimeout(() => {
                    setEyebrowPhase('idle');
                    eyebrowTimeoutRef.current = null;
                }, EYEBROW_ENTER_MS);
                return;
            }

            if (eyebrowPhase === 'enter') {
                eyebrowTimeoutRef.current = setTimeout(() => {
                    setEyebrowPhase('idle');
                    eyebrowTimeoutRef.current = null;
                }, EYEBROW_ENTER_MS);
            }
            return;
        }

        if (renderedEyebrow) {
            setEyebrowPhase('exit');

            eyebrowTimeoutRef.current = setTimeout(() => {
                setRenderedEyebrow(null);
                setEyebrowPhase('hidden');
                eyebrowTimeoutRef.current = null;
            }, EYEBROW_EXIT_MS);
        } else {
            setEyebrowPhase('hidden');
        }
    }, [eyebrow, eyebrowKey, eyebrowPhase, eyebrowTone, renderedEyebrow]);

    return (
        <div className={classNames(
            "flex w-full flex-col gap-2",
            className
        )}>
            <div className="w-full">
                <div className={classNames(renderedEyebrow ? "group relative w-full" : null)}>
                    {renderedEyebrow
                        ? <div
                            className={classNames(
                                defaultEyebrowClasses(renderedEyebrow.tone),
                                eyebrowPhase === 'enter' ? "auction-eyebrow-chip-enter" : null,
                                eyebrowPhase === 'exit' ? "auction-eyebrow-chip-exit" : null,
                                eyebrowClassName
                            )}
                            data-title=""
                        >
                            <span
                                className={classNames(
                                    "auction-eyebrow-content",
                                    eyebrowPhase === 'enter' ? "auction-eyebrow-enter" : null,
                                    eyebrowPhase === 'exit' ? "auction-eyebrow-exit" : null
                                )}
                            >
                                {renderEyebrowDot(renderedEyebrow.tone, eyebrowDotClassName)}
                                <span className="block min-w-0 truncate text-center">{renderedEyebrow.label}</span>
                            </span>
                        </div>
                        : null}

                    {children}
                </div>
            </div>

            {helperText
                ? <div className={classNames(
                    "text-center text-sm leading-tight text-white/80",
                    helperClassName
                )} data-body="">
                    {helperText}
                </div>
                : null}
        </div>
    );
}
